import React, { useEffect, useRef } from 'react'
import { Platform, ScrollView, ScrollViewProps } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { AutocompleteInputProvider, useAutocompleteInputContext } from './AutocompleteInputProvider'

export const onWindowMeasure = (y: number | undefined, onResult: (y: number) => void) => {
  if (!!y) {
    onResult(y)
  }
}

export const wrapScrollView = <T extends ScrollViewProps>(Component: React.ComponentClass<T>) => {
  const AnimatedComponent = Animated.createAnimatedComponent(Component)
  const WrappedScrollView = (props: T) => {
    const { isDropdownOpened, setScreenTop } = useAutocompleteInputContext()
    const dropdownOpenedAnim = useSharedValue(0)
    const scrollViewRef = useRef<ScrollView | null>(null)

    useEffect(() => {
      if (Platform.OS !== 'web') {
        dropdownOpenedAnim.value = withTiming(isDropdownOpened ? 1 : 0, { duration: 150 })
      }
    }, [isDropdownOpened, dropdownOpenedAnim])

    useEffect(() => {
      // @ts-expect-error wrong type defs in reanimated.
      scrollViewRef.current?.measureInWindow((_, y) => onWindowMeasure(y, setScreenTop))
    })

    const animatedStyle = useAnimatedStyle(() => ({
      opacity: interpolate(dropdownOpenedAnim.value, [0, 1], [1, 0]),
    }))

    return (
      <AnimatedComponent
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
        ref={scrollViewRef}
        keyboardShouldPersistTaps={isDropdownOpened ? 'always' : props.keyboardShouldPersistTaps}
        style={[props.style, animatedStyle]}
      />
    )
  }

  const WrappedScrollViewWithProvider = (props: T) => {
    return (
      <AutocompleteInputProvider>
        <WrappedScrollView {...props} />
      </AutocompleteInputProvider>
    )
  }

  return WrappedScrollViewWithProvider
}

export const AutocompleteInputScrollView = wrapScrollView(ScrollView)
